import React from "react";

import hb01 from "../../../../assets/home/journeys/homebuyer/01.webp";
import hb02 from "../../../../assets/home/journeys/homebuyer/02.webp";
import hb03 from "../../../../assets/home/journeys/homebuyer/03.webp";
import hb04 from "../../../../assets/home/journeys/homebuyer/04.webp";

import oss01 from "../../../../assets/home/journeys/oss/01.webp";
import oss02 from "../../../../assets/home/journeys/oss/02.webp";
import oss03 from "../../../../assets/home/journeys/oss/03.webp";
import oss04 from "../../../../assets/home/journeys/oss/04.webp";

import lender01 from "../../../../assets/home/journeys/lender/01.webp";
import lender02 from "../../../../assets/home/journeys/lender/02.webp";
import lender03 from "../../../../assets/home/journeys/lender/03.webp";
import lender04 from "../../../../assets/home/journeys/lender/04.webp";

import investor01 from "../../../../assets/home/journeys/investor/01.webp";
import investor02 from "../../../../assets/home/journeys/investor/02.webp";
import investor03 from "../../../../assets/home/journeys/investor/03.webp";
import investor04 from "../../../../assets/home/journeys/investor/04.webp";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import {
  House,
  Wrench,
  Landmark,
  ChartNoAxesCombined,
  ArrowUpRight,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import "./RoleJourneySection.css";

gsap.registerPlugin(ScrollTrigger);

const STEP_DURATION_MS = 5000;

/* =========================================================
   ROLES
   ========================================================= */

const ROLES = [
  {
    key: "homebuyer",

    Icon: House,

    fallbackLabel: "Homebuyer",

    images: [hb01, hb02, hb03, hb04],
  },

  {
    key: "oss",

    Icon: Wrench,

    fallbackLabel: "One-Stop-Shop",

    images: [oss01, oss02, oss03, oss04],
  },

  {
    key: "lender",

    Icon: Landmark,

    fallbackLabel: "Lender",

    images: [lender01, lender02, lender03, lender04],
  },

  {
    key: "investor",

    Icon: ChartNoAxesCombined,

    fallbackLabel: "Investor",

    images: [investor01, investor02, investor03, investor04],
  },
];

/* =========================================================
   FALLBACK STEPS
   ========================================================= */

const FALLBACK_STEPS = {
  homebuyer: [
    {
      title: "Build your digital dossier",
      description:
        "Add your personal, financial and property information, upload the required documents and create the shared starting point for your renovation and financing journey.",
      timeline: "Build your dossier",
    },

    {
      title: "Connect with the right support",
      description:
        "Connect with participating One-Stop-Shops and lenders so renovation planning and financing can move forward through the same coordinated process.",
      timeline: "Connect with support",
    },

    {
      title: "Align renovation and financing",
      description:
        "Review the renovation plan and financing options, including lender decisions, loan terms and complementary funding sources where relevant.",
      timeline: "Review the plan & finance",
    },

    {
      title: "Renovate and stay on track",
      description:
        "Follow the funded renovation as it progresses, keeping project information, funding, invoices and key updates connected in one place.",
      timeline: "Manage the renovation",
    },
  ],

  oss: [
    {
      title: "Receive the homeowner request",
      description:
        "Start from the homeowner's shared project context and understand the property, renovation needs and support required to move the project forward.",
      timeline: "Receive the request",
    },

    {
      title: "Shape the renovation plan",
      description:
        "Develop and organise the renovation approach, relevant services and project information that will support both implementation and financing.",
      timeline: "Plan the renovation",
    },

    {
      title: "Coordinate the project",
      description:
        "Keep the homeowner, project information and supporting documents aligned while the financing and implementation pathway takes shape.",
      timeline: "Coordinate support",
    },

    {
      title: "Support delivery and progress",
      description:
        "Follow the renovation through implementation, helping keep project updates, documentation and the wider renovation journey coordinated.",
      timeline: "Support delivery",
    },
  ],

  lender: [
    {
      title: "Link or create the loan case",
      description:
        "Link an existing homeowner dossier to a loan case on the platform, or create a manual loan case for financing that started outside the standard LEG-UP flow.",
      timeline: "Open the case",
    },

    {
      title: "Record the initial financing assessment",
      description:
        "Review the applicant, property and financial information and record either an initial financing estimate or the loan case result before the renovation plan is finalised.",
      timeline: "Assess the financing",
    },

    {
      title: "Finalise renovation financing",
      description:
        "Review the renovation plan and confirm the final approved renovation financing, including the lender contribution and complementary funding sources where applicable.",
      timeline: "Finalise the finance",
    },

    {
      title: "Track and allocate renovation invoices",
      description:
        "Follow renovation invoices during implementation and allocate each invoice across the available funding sources so project financing remains transparent and traceable.",
      timeline: "Track the invoices",
    },
  ],

  investor: [
    {
      title: "Understand the renovation opportunity",
      description:
        "See how renovation demand, financing needs and energy-efficiency investment come together across the LEG-UP ecosystem.",
      timeline: "Understand the opportunity",
    },

    {
      title: "Review the financing pathway",
      description:
        "Follow how renovation projects move from homeowner demand and technical planning into structured financing opportunities.",
      timeline: "Review the pathway",
    },

    {
      title: "Support renovation capital",
      description:
        "Connect capital with renovation finance through the lending and investment structures that can help scale energy-efficient housing upgrades.",
      timeline: "Support the capital",
    },

    {
      title: "Follow performance and impact",
      description:
        "Track the wider value created by renovation finance, from deployed capital and project delivery to long-term energy and housing outcomes.",
      timeline: "Follow the impact",
    },
  ],
};

/* =========================================================
   ROLE PILL
   ========================================================= */

function RolePill({ role, active, onClick, label }) {
  const Icon = role.Icon;

  return (
    <button
      type="button"
      className={`role-journey__role-pill${active ? " is-active" : ""}`}
      onClick={onClick}
      aria-pressed={active}
    >
      {active ? (
        <motion.span
          layoutId="role-journey-pill-active"
          className="role-journey__role-pill-active"
          transition={{
            type: "spring",
            stiffness: 420,
            damping: 34,
          }}
        />
      ) : null}

      <span className="role-journey__role-pill-content">
        <Icon size={17} strokeWidth={1.9} aria-hidden="true" />

        <span>{label}</span>
      </span>
    </button>
  );
}

/* =========================================================
   COMPONENT
   ========================================================= */

export default function RoleJourneySection() {
  const { t, i18n } = useTranslation("home");

  const prefersReducedMotion = useReducedMotion();

  /* =========================================================
     REFS
     ========================================================= */

  const sectionRef = React.useRef(null);

  const backgroundRef = React.useRef(null);

  const innerRef = React.useRef(null);

  /* =========================================================
     STATE
     ========================================================= */

  const [activeRoleIndex, setActiveRoleIndex] = React.useState(0);

  const [activeStepIndex, setActiveStepIndex] = React.useState(0);

  const [cycleKey, setCycleKey] = React.useState(0);

  /* =========================================================
     ACTIVE DATA
     ========================================================= */

  const activeRole = ROLES[activeRoleIndex];

  const roleSteps = FALLBACK_STEPS[activeRole.key];

  const activeFallbackStep = roleSteps[activeStepIndex];

  const activeStep = {
    title: t(
      `journey.roles.${activeRole.key}.steps.${activeStepIndex}.title`,

      activeFallbackStep.title,
    ),

    description: t(
      `journey.roles.${activeRole.key}.steps.${activeStepIndex}.description`,

      activeFallbackStep.description,
    ),

    timeline: t(
      `journey.roles.${activeRole.key}.steps.${activeStepIndex}.timeline`,

      activeFallbackStep.timeline,
    ),

    image: activeRole.images[activeStepIndex],
  };

  const roleLabel = (role) =>
    t(
      `journey.roles.${role.key}.label`,

      role.fallbackLabel,
    );

  /* =========================================================
     STEP CYCLE
     ========================================================= */

  const resetCycle = React.useCallback(() => {
    setCycleKey((value) => value + 1);
  }, []);

  const selectRole = React.useCallback(
    (roleIndex) => {
      setActiveRoleIndex(roleIndex);

      setActiveStepIndex(0);

      resetCycle();
    },

    [resetCycle],
  );

  const selectStep = React.useCallback(
    (stepIndex) => {
      setActiveStepIndex(stepIndex);

      resetCycle();
    },

    [resetCycle],
  );

  const goToNextStep = React.useCallback(() => {
    setActiveStepIndex((current) => (current + 1) % 4);

    resetCycle();
  }, [resetCycle]);

  /* =========================================================
     AUTO STEP
     ========================================================= */

  React.useEffect(() => {
    if (prefersReducedMotion) {
      return undefined;
    }

    const timer = window.setTimeout(
      goToNextStep,

      STEP_DURATION_MS,
    );

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    activeRoleIndex,
    activeStepIndex,
    cycleKey,
    goToNextStep,
    prefersReducedMotion,
  ]);

  /* =========================================================
     LANGUAGE CHANGE
     ========================================================= */

  React.useEffect(() => {
    resetCycle();
  }, [i18n.resolvedLanguage, resetCycle]);

  React.useLayoutEffect(() => {
    const section = sectionRef.current;

    const inner = innerRef.current;

    if (!section || !inner) {
      return undefined;
    }

    const reducedMotion = prefersReducedMotion;

    const header = inner.querySelector(".role-journey__header");

    const eyebrow = inner.querySelector(".role-journey__eyebrow");

    const title = inner.querySelector(".role-journey__title");

    const intro = inner.querySelector(".role-journey__intro");

    const roles = inner.querySelector(".role-journey__roles");

    const contentArea = inner.querySelector(".role-journey__content-area");

    const timeline = inner.querySelector(".role-journey__timeline");

    const animatedElements = [
      eyebrow,
      title,
      intro,
      roles,
      contentArea,
      timeline,
    ].filter(Boolean);

    /*
     * Kill any old GSAP inline animation state
     * left by the previous ScrollTrigger version.
     */
    gsap.killTweensOf(animatedElements);

    gsap.set(animatedElements, {
      autoAlpha: 1,
      y: 0,
      scale: 1,
    });

    if (reducedMotion) {
      return undefined;
    }

    const clamp = gsap.utils.clamp(0, 1);

    /* =======================================================
     LIVE VIEWPORT REVEAL

     Uses getBoundingClientRect() every time.

     NO ScrollTrigger measurements.
     NO cached positions.
     ======================================================= */

    const groups = [];

    const addReveal = ({
      trigger,
      targets,
      amount = 34,
      scale = 0.985,
      enterSize = 0.15,
      exitSize = 0.15,
    }) => {
      const elements = Array.isArray(targets)
        ? targets.filter(Boolean)
        : [targets].filter(Boolean);

      if (!trigger || !elements.length) {
        return;
      }

      groups.push({
        trigger,
        elements,
        amount,
        scale,
        enterSize,
        exitSize,
      });
    };

    /* ---------------- HEADER ---------------- */

    addReveal({
      trigger: header,

      targets: [eyebrow, title, intro],

      amount: 32,

      scale: 0.99,
    });

    /* ---------------- ACTOR SELECTOR ---------------- */

    addReveal({
      trigger: roles,

      targets: roles,

      amount: 28,

      scale: 0.985,
    });

    /* ---------------- ACTIVE STEP CARD ---------------- */

    addReveal({
      trigger: contentArea,

      targets: contentArea,

      amount: 42,

      scale: 0.975,
    });

    /* ---------------- TIMELINE ---------------- */

    addReveal({
      trigger: timeline,

      targets: timeline,

      amount: 34,

      scale: 0.985,
    });

    /* =======================================================
     UPDATE
     ======================================================= */

    const update = () => {
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      groups.forEach(
        ({ trigger, elements, amount, scale, enterSize, exitSize }) => {
          const rect = trigger.getBoundingClientRect();

          /*
           * Progress through viewport:
           *
           * 0 = top of element reaches bottom
           * 1 = bottom of element reaches top
           */
          const travel = viewportHeight + rect.height;

          const progress = clamp((viewportHeight - rect.top) / travel);

          let opacity = 1;

          let y = 0;

          let currentScale = 1;

          /* =============================================
             FIRST 15% — ENTER FROM BELOW
             ============================================= */

          if (progress < enterSize) {
            const q = clamp(progress / enterSize);

            opacity = q;

            y = amount * (1 - q);

            currentScale = scale + (1 - scale) * q;
          } else if (progress > 1 - exitSize) {
            /* =============================================
             LAST 15% — EXIT THROUGH TOP
             ============================================= */
            const q = clamp((progress - (1 - exitSize)) / exitSize);

            opacity = 1 - q;

            y = -amount * q;

            currentScale = 1 - (1 - scale) * q;
          }

          gsap.set(elements, {
            autoAlpha: opacity,

            y,

            scale: currentScale,

            force3D: true,
          });
        },
      );
    };

    /* =======================================================
     RAF-THROTTLED SCROLL
     ======================================================= */

    let frame = null;

    const scheduleUpdate = () => {
      if (frame) {
        return;
      }

      frame = requestAnimationFrame(() => {
        frame = null;

        update();
      });
    };

    window.addEventListener("scroll", scheduleUpdate, {
      passive: true,
    });

    window.addEventListener("resize", scheduleUpdate);

    requestAnimationFrame(update);

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }

      window.removeEventListener("scroll", scheduleUpdate);

      window.removeEventListener("resize", scheduleUpdate);

      gsap.set(animatedElements, {
        autoAlpha: 1,
        y: 0,
        scale: 1,
      });
    };
  }, [prefersReducedMotion]);
  /* =========================================================
     ACTIVE ICON
     ========================================================= */

  const ActiveRoleIcon = activeRole.Icon;

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <section
      ref={sectionRef}
      className="role-journey"
      aria-labelledby="role-journey-title"
    >
      {/* =====================================================
          BACKGROUND

          IMPORTANT:
          No ScrollTrigger / GSAP viewport animation.
          Only the existing Framer Motion transition between
          journey images remains.
          ===================================================== */}

      <div
        ref={backgroundRef}
        className="role-journey__background"
        aria-hidden="true"
      >
        <AnimatePresence initial={false} mode="sync">
          <motion.div
            key={`${activeRole.key}-${activeStepIndex}-background`}
            className="role-journey__background-slide"
            initial={
              prefersReducedMotion
                ? false
                : {
                    opacity: 0,

                    scale: 1.045,
                  }
            }
            animate={{
              opacity: 1,

              scale: 1,
            }}
            exit={
              prefersReducedMotion
                ? undefined
                : {
                    opacity: 0,

                    scale: 1.018,
                  }
            }
            transition={{
              opacity: {
                duration: 0.7,

                ease: [0.22, 1, 0.36, 1],
              },

              scale: {
                duration: 1.15,

                ease: [0.22, 1, 0.36, 1],
              },
            }}
          >
            <img
              src={activeStep.image}
              alt=""
              className="role-journey__background-image"
            />
          </motion.div>
        </AnimatePresence>

        <div className="role-journey__background-wash" />

        <div className="role-journey__background-vignette" />
      </div>

      {/* =====================================================
          FOREGROUND
          ===================================================== */}

      <div ref={innerRef} className="role-journey__inner">
        {/* ===================================================
            HEADER
            =================================================== */}

        <header className="role-journey__header">
          <span className="role-journey__eyebrow">
            {t(
              "journey.eyebrow",

              "THE LEG-UP JOURNEY",
            )}
          </span>

          <h2 id="role-journey-title" className="role-journey__title">
            {t(
              "journey.title.prefix",

              "See how each actor moves through",
            )}{" "}
            <strong>
              {t(
                "journey.title.accent",

                "LEG-UP",
              )}
            </strong>
          </h2>

          <p className="role-journey__intro">
            {t(
              "journey.intro",

              "Switch between actors to explore how LEG-UP connects the different steps of renovation, support and financing.",
            )}
          </p>
        </header>

        {/* ===================================================
            ROLE SELECTOR
            =================================================== */}

        <nav
          className="role-journey__roles"
          aria-label={t(
            "journey.roleNavigationLabel",

            "Choose an actor",
          )}
        >
          {ROLES.map((role, index) => (
            <RolePill
              key={role.key}
              role={role}
              label={roleLabel(role)}
              active={activeRoleIndex === index}
              onClick={() => selectRole(index)}
            />
          ))}
        </nav>

        {/* ===================================================
            ACTIVE STEP CARD
            =================================================== */}

        <div className="role-journey__content-area">
          <AnimatePresence mode="wait" initial={false}>
            <motion.article
              key={`${activeRole.key}-${activeStepIndex}-copy`}
              className="role-journey__copy"
              initial={
                prefersReducedMotion
                  ? false
                  : {
                      opacity: 0,

                      y: 20,
                    }
              }
              animate={{
                opacity: 1,

                y: 0,
              }}
              exit={
                prefersReducedMotion
                  ? undefined
                  : {
                      opacity: 0,

                      y: -12,
                    }
              }
              transition={{
                duration: 0.46,

                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <div className="role-journey__copy-topline">
                <div className="role-journey__step-label">
                  {String(activeStepIndex + 1).padStart(2, "0")}

                  <span>/ 04</span>
                </div>

                <div className="role-journey__active-role">
                  <ActiveRoleIcon
                    size={16}
                    strokeWidth={1.9}
                    aria-hidden="true"
                  />

                  <span>{roleLabel(activeRole)}</span>
                </div>
              </div>

              <h3 className="role-journey__step-title">{activeStep.title}</h3>

              <p className="role-journey__step-description">
                {activeStep.description}
              </p>

              <button
                type="button"
                className="role-journey__next"
                onClick={goToNextStep}
              >
                <span>
                  {t(
                    "journey.nextStep",

                    "Next step",
                  )}
                </span>

                <ArrowUpRight size={17} strokeWidth={2} aria-hidden="true" />
              </button>
            </motion.article>
          </AnimatePresence>
        </div>

        {/* ===================================================
            TIMELINE
            =================================================== */}

        <div
          className="role-journey__timeline"
          aria-label={t(
            "journey.timelineLabel",

            "Journey steps",
          )}
        >
          {roleSteps.map((fallbackStep, index) => {
            const active = activeStepIndex === index;

            const completed = index < activeStepIndex;

            const label = t(
              `journey.roles.${activeRole.key}.steps.${index}.timeline`,

              fallbackStep.timeline,
            );

            return (
              <button
                key={`${activeRole.key}-${index}`}
                type="button"
                className={`role-journey__timeline-step${
                  active ? " is-active" : ""
                }${completed ? " is-complete" : ""}`}
                onClick={() => selectStep(index)}
                aria-current={active ? "step" : undefined}
              >
                <span className="role-journey__timeline-number">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <span className="role-journey__timeline-title">{label}</span>

                <span
                  className="role-journey__timeline-track"
                  aria-hidden="true"
                >
                  <span
                    key={
                      active
                        ? `${activeRole.key}-${activeStepIndex}-${cycleKey}`
                        : `inactive-${activeRole.key}-${index}`
                    }
                    className="role-journey__timeline-fill"
                    style={
                      active && !prefersReducedMotion
                        ? {
                            animationDuration: `${STEP_DURATION_MS}ms`,
                          }
                        : undefined
                    }
                  />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
